import {useState} from 'react';
import {cn} from '@/matic/lib/utils/cn';
import './pony-express.css';

/**
 * One stride of the rider as a strip of traced-vector frames, so it stays sharp at any
 * zoom; see public/quoting/ and pony-express.css.
 */
const FRAMES = 17;
const FRAME_RATIO = 1049 / 619;
/** One stride; matches `pony-stride` in pony-express.css and `stride` in index.html. */
const STRIDE_MS = 850;
const STRIP = '/pony-gallop.svg';

type Props = {
  /** Rendered height in pixels; the width follows the frame. */
  size?: number;
  className?: string;
};

/**
 * The Pony Express rider galloping in place: MATIC is carrying something through.
 * Reduced motion shows the rider still.
 */
export function PonyExpress({size = 20, className}: Props) {
  // A whole-pixel frame width with the height taken from it exactly: a rounded width
  // over a fixed height letterboxes the strip, so each step lands a fraction off its
  // frame and the neighbouring rider shows through at small sizes.
  const width = Math.max(1, Math.round(size * FRAME_RATIO));
  // Every rider keeps the page's clock rather than starting its stride when it mounts,
  // so a rider that replaces another (the boot screen handing over to React's, a
  // Suspense fallback remounting) carries on mid-stride instead of jumping to frame 0.
  const [delay] = useState(
    () => `-${Math.round(performance.now() % STRIDE_MS)}ms`
  );
  return (
    <span
      role="img"
      aria-label="Working"
      className={cn('pony-gallop', className)}
      style={{height: width / FRAME_RATIO, width}}
    >
      <img
        src={STRIP}
        alt=""
        className="pony-strip"
        style={{width: `${FRAMES * 100}%`, animationDelay: delay}}
      />
    </span>
  );
}
